import {
  Award,
  Crown,
  Network,
  Shield,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { Reveal } from '../components/Reveal';

const STREAMS = [
  {
    icon: Users,
    title: 'Direct Referral Bonus',
    desc: 'Earn an LDS reward on every direct connection you bring into the ecosystem.',
  },
  {
    icon: Network,
    title: 'Level Income',
    desc: 'Earn from your team’s growth across 7 levels as your network refers new members.',
  },
  {
    icon: ShieldCheck,
    title: 'Alpha Club (A) Income',
    desc: 'Qualify through your direct network’s growth and unlock the Alpha rank ladder.',
  },
  {
    icon: Shield,
    title: 'Alpha Club (B) Income',
    desc: 'Build a stronger B-team of qualified Alpha members to unlock premium B-Club rewards.',
  },
  {
    icon: Award,
    title: 'Leadership Bonus',
    desc: 'Balanced network growth on both sides of your team unlocks achievement milestones.',
  },
  {
    icon: Crown,
    title: 'Elite Rank Benefits',
    desc: 'Reach Silver, Gold or Diamond status for recurring monthly LDS rewards and perks.',
  },
];

const LEVELS = [
  { level: 1, bonus: 6 },
  { level: 2, bonus: 5 },
  { level: 3, bonus: 4 },
  { level: 4, bonus: 2 },
  { level: 5, bonus: 1 },
  { level: 6, bonus: 1 },
  { level: 7, bonus: 1 },
];

const ALPHA_CLUB_A = [
  { rank: 'Alpha 1', gift: 50 },
  { rank: 'Alpha 2', gift: 150 },
  { rank: 'Alpha 3', gift: 250 },
  { rank: 'Alpha 4', gift: 500 },
  { rank: 'Alpha 5', gift: 750 },
  { rank: 'Alpha 6', gift: 1250 },
  { rank: 'Alpha 7', gift: 1500 },
];

const ALPHA_CLUB_B = [
  { rank: 'B1', gift: '1,000' },
  { rank: 'B2', gift: '3,000' },
  { rank: 'B3', gift: '5,000' },
  { rank: 'B4', gift: '10,000' },
  { rank: 'B5', gift: '15,000' },
  { rank: 'B6', gift: '25,000' },
  { rank: 'B7', gift: '30,000' },
];

const ELITE_RANKS = [
  { name: 'Silver', qualifies: 'B5 Club', amount: '$50', months: 'per month × 10 months', color: '#c0c0c0' },
  { name: 'Gold', qualifies: 'B6 Club', amount: '$100', months: 'per month × 15 months', color: 'var(--gold)' },
  {
    name: 'Diamond',
    qualifies: 'B7 Club',
    amount: '$300',
    months: 'per month × 20 months + premium SUV reward',
    color: '#8ec9ff',
  },
];

export function IncomeStreams() {
  return (
    <>
      <section id="ecosystem" className="section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow" style={{ justifyContent: 'center' }}>
              Token Utility &amp; Ecosystem
            </span>
            <h2 className="section-title">
              One token. <span className="accent">Every reward.</span>
            </h2>
            <p className="section-desc">
              The <strong>LDS Token</strong> powers the entire Alpha One ecosystem — from
              activation and income to internal transfers and future real-world utility.
            </p>
          </Reveal>

          <div className="grid grid-3">
            {STREAMS.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <div className="card">
                  <div className="card-icon">
                    <s.icon size={24} />
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="income" className="section section-alt">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow" style={{ justifyContent: 'center' }}>
              Compensation Plan Highlights
            </span>
            <h2 className="section-title">
              Multiple income streams. <span className="accent">Endless opportunities.</span>
            </h2>
            <p className="section-desc">
              All income and rewards are distributed in LDS Tokens according to the Alpha One
              compensation plan.
            </p>
          </Reveal>

          <div className="grid grid-2" style={{ marginBottom: '3rem' }}>
            <Reveal>
              <div className="card">
                <h3>Direct Referral Bonus</h3>
                <p style={{ marginBottom: '1.25rem' }}>
                  Earn more with every direct connection you bring into Alpha One.
                </p>
                <div className="tier-row">
                  <div className="tier-chip">
                    <b>50%</b>
                    <span>Direct 1 &amp; 2</span>
                  </div>
                  <div className="tier-chip">
                    <b>30%</b>
                    <span>Direct 3 and above</span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="card">
                <h3>Level Income</h3>
                <p style={{ marginBottom: '1.25rem' }}>
                  Starting from Level 1, every new referral from your team continues the chain
                  down to Level 7.
                </p>
                <div className="tier-row">
                  {LEVELS.map((l) => (
                    <div className="tier-chip" key={l.level}>
                      <b>${l.bonus}</b>
                      <span>Level {l.level}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-2" style={{ marginBottom: '3rem' }}>
            <Reveal>
              <div className="card">
                <h3>Alpha Club (A)</h3>
                <p style={{ marginBottom: '1.25rem' }}>
                  Qualify by growing your direct network through 7 levels to unlock the Alpha
                  rank ladder and its LDS gift rewards.
                </p>
                <div className="tier-row">
                  {ALPHA_CLUB_A.map((r) => (
                    <div className="tier-chip" key={r.rank}>
                      <b>${r.gift}</b>
                      <span>{r.rank}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="card">
                <h3>Alpha Club (B)</h3>
                <p style={{ marginBottom: '1.25rem' }}>
                  Qualification is achieved through qualified Alpha members in your network,
                  unlocking the premium B-Club reward tiers.
                </p>
                <div className="tier-row">
                  {ALPHA_CLUB_B.map((r) => (
                    <div className="tier-chip" key={r.rank}>
                      <b>{r.gift}</b>
                      <span>{r.rank} LDS</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="section-head center" style={{ marginTop: '1rem' }}>
            <span className="eyebrow" style={{ justifyContent: 'center' }}>
              Elite Rank Benefits
            </span>
            <h2 className="section-title">
              Higher rank. <span className="accent">Premium rewards.</span>
            </h2>
          </Reveal>

          <div className="grid grid-3">
            {ELITE_RANKS.map((r, i) => (
              <Reveal key={r.name} delay={i * 80}>
                <div
                  className="card elite-card"
                  style={{ '--tier-color': r.color } as CSSProperties}
                >
                  <div className="rank-name">{r.name}</div>
                  <div className="rank-amount">{r.amount}</div>
                  <div className="rank-months">{r.months}</div>
                  <p>Unlocked by reaching {r.qualifies}, paid monthly in LDS Tokens.</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
